// Video through the browser's own element, with the things it does not give
// you: fit modes for any aspect ratio, speed, picture in picture, a frame
// capture, and diagnostics read from the element itself. HLS plays natively
// where the browser can, through a host-supplied player where it cannot, and
// says so where neither is available.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { SegmentedControl } from '../../inputs/SegmentedControl/SegmentedControl.js';
import { Select } from '../../inputs/Select/Select.js';
import { downloadBlob } from '@repletoryreach/sdk-utils/file';
import { IconButton, Notice, Toolbar, ToolbarGroup, ToolbarText } from '../chrome.js';
import { IconCamera, IconLoop, IconPip } from '../icons.js';
import { formatAspect, formatBytes, formatDuration } from '../format.js';
import { isDashManifest, isHlsManifest } from '../detect.js';
import type { HlsInstance, RendererProps } from '../types.js';

type Fit = 'contain' | 'cover' | 'original';
const SPEEDS = ['0.5', '0.75', '1', '1.25', '1.5', '2'];

interface Diagnostics {
  width: number; height: number; duration: number; buffered: number; dropped: number | null; total: number | null;
  protocol: string; bandwidth: number | null; levels: Array<{ index: number; label: string }>; level: number; live: boolean;
}

export function VideoRenderer({ file, detected, source, labels, register, announce, loaders, download, openExternally }: RendererProps): ReactNode {
  const video = useRef<HTMLVideoElement | null>(null);
  const hls = useRef<HlsInstance | null>(null);
  const [fit, setFit] = useState<Fit>('contain');
  const [speed, setSpeed] = useState('1');
  const [loop, setLoop] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [diag, setDiag] = useState<Diagnostics>({ width: 0, height: 0, duration: 0, buffered: 0, dropped: null, total: null, protocol: 'progressive', bandwidth: null, levels: [], level: -1, live: false });

  const manifest = isHlsManifest(detected);
  const dash = isDashManifest(detected);
  const url = file.streamUrl ?? file.objectUrl ?? '';
  // A manifest fetched with headers arrived as a blob, and its segment URLs are
  // relative to a location the media element cannot resolve. Only a real URL plays.
  const hlsUrl = manifest ? file.streamUrl : null;
  const nativeHls = typeof document !== 'undefined' && !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');

  // Attach the source: native, HLS native, HLS via loader, or give up honestly.
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let cancelled = false;
    setFailed(null);
    if (dash) { setFailed(labels.unsupportedHls); return; }
    if (!manifest) {
      el.src = url;
      setDiag((d) => ({ ...d, protocol: 'progressive' }));
      return;
    }
    if (!hlsUrl) { setFailed(labels.unsupportedHls); return; }
    if (nativeHls) {
      el.src = hlsUrl;
      setDiag((d) => ({ ...d, protocol: labels.hlsNative }));
      return;
    }
    if (!loaders?.hls) { setFailed(labels.unsupportedHls); return; }
    loaders.hls().then((mod) => {
      if (cancelled) return;
      const Hls = mod.default;
      if (!Hls.isSupported()) { setFailed(labels.unsupportedHls); return; }
      const instance = new Hls({ enableWorker: true });
      hls.current = instance;
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        setDiag((d) => ({
          ...d,
          protocol: labels.hlsPlayer,
          levels: instance.levels.map((l, i) => ({ index: i, label: `${l.height ?? '?'}p${l.bitrate ? ` · ${Math.round(l.bitrate / 1000)} kbps` : ''}` }))
        }));
      });
      instance.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
        const level = (data as { level?: number })?.level ?? -1;
        setDiag((d) => ({ ...d, level, bandwidth: instance.bandwidthEstimate ?? null }));
      });
      instance.on(Hls.Events.ERROR, (_e, data) => {
        const fatal = (data as { fatal?: boolean })?.fatal;
        if (fatal) setFailed(labels.mediaPlaybackFailed);
      });
      instance.loadSource(hlsUrl);
      instance.attachMedia(el);
    }).catch(() => { if (!cancelled) setFailed(labels.unsupportedHls); });
    return () => {
      cancelled = true;
      hls.current?.destroy();
      hls.current = null;
    };
  }, [url, hlsUrl, manifest, dash, nativeHls, loaders, labels]);

  useEffect(() => { if (video.current) video.current.playbackRate = Number(speed); }, [speed]);
  useEffect(() => { if (video.current) video.current.loop = loop; }, [loop]);

  const readDiagnostics = useCallback(() => {
    const el = video.current;
    if (!el) return;
    let buffered = 0;
    for (let i = 0; i < el.buffered.length; i++) {
      if (el.buffered.start(i) <= el.currentTime && el.currentTime <= el.buffered.end(i)) buffered = el.buffered.end(i) - el.currentTime;
    }
    const q = typeof el.getVideoPlaybackQuality === 'function' ? el.getVideoPlaybackQuality() : null;
    setDiag((d) => ({
      ...d,
      width: el.videoWidth, height: el.videoHeight, duration: el.duration,
      buffered, dropped: q ? q.droppedVideoFrames : null, total: q ? q.totalVideoFrames : null,
      live: el.duration === Infinity
    }));
  }, []);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    const timer = window.setInterval(readDiagnostics, 1000);
    el.addEventListener('loadedmetadata', readDiagnostics);
    return () => { window.clearInterval(timer); el.removeEventListener('loadedmetadata', readDiagnostics); };
  }, [readDiagnostics]);

  // A frame from a blob or same-origin stream draws cleanly; a cross-origin stream taints the canvas.
  const canCapture = !!file.blob || (!!file.streamUrl && sameOrigin(file.streamUrl));
  const pipAvailable = typeof document !== 'undefined' && 'pictureInPictureEnabled' in document && (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled === true;

  const captureFrame = useCallback(() => {
    const el = video.current;
    if (!el || !el.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = el.videoWidth;
    canvas.height = el.videoHeight;
    canvas.getContext('2d')?.drawImage(el, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const base = file.name.replace(/\.[^.]+$/, '') || 'frame';
      downloadBlob(blob, `${base}-${formatDuration(el.currentTime).replace(/:/g, '-')}.png`);
      announce(labels.captureFrame);
    }, 'image/png');
  }, [file.name, announce, labels.captureFrame]);

  const togglePip = useCallback(() => {
    const el = video.current as (HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown> }) | null;
    if (!el) return;
    const doc = document as Document & { pictureInPictureElement?: Element | null; exitPictureInPicture?: () => Promise<void> };
    if (doc.pictureInPictureElement) void doc.exitPictureInPicture?.();
    else void el.requestPictureInPicture?.();
  }, []);

  useEffect(() => {
    const rows = [];
    if (diag.width) rows.push({ label: labels.detailResolution, value: `${diag.width} × ${diag.height} (${formatAspect(diag.width, diag.height)})` });
    if (diag.duration && Number.isFinite(diag.duration)) rows.push({ label: labels.detailDuration, value: formatDuration(diag.duration) });
    rows.push({ label: labels.protocol, value: diag.protocol });
    if (diag.live) rows.push({ label: labels.live, value: labels.yes });
    rows.push({ label: labels.bufferHealth, value: `${diag.buffered.toFixed(1)} s` });
    if (diag.dropped != null) rows.push({ label: labels.droppedFrames, value: `${diag.dropped}${diag.total != null ? ` / ${diag.total}` : ''}` });
    if (diag.bandwidth != null) rows.push({ label: labels.bandwidth, value: `${formatBytes(diag.bandwidth / 8)}/s` });
    rows.push({ label: labels.detailRenderer, value: labels.rendererNative });
    register({
      capabilities: { fullscreen: true, pictureInPicture: pipAvailable, quality: diag.levels.length > 0, exportPreview: canCapture },
      actions: { exportPreview: canCapture ? captureFrame : undefined },
      details: rows,
      status: diag.width ? `${diag.width} × ${diag.height}${diag.duration && Number.isFinite(diag.duration) ? ` · ${formatDuration(diag.duration)}` : ''}` : undefined,
      onKey: (e) => {
        const el = video.current;
        if (!el) return false;
        if (e.key === ' ') { if (el.paused) void el.play(); else el.pause(); return true; }
        if (e.key === 'ArrowLeft') { el.currentTime = Math.max(0, el.currentTime - 10); return true; }
        if (e.key === 'ArrowRight') { el.currentTime = Math.min(el.duration || Infinity, el.currentTime + 10); return true; }
        return false;
      }
    });
  }, [register, diag, labels, pipAvailable, canCapture, captureFrame]);

  if (failed) {
    return (
      <Notice family="video" title={labels.unsupportedTitle} message={failed} labels={labels} download={download} openExternally={openExternally}>
        {file.link && manifest && (
          <div className="rr-fv__notice-extra"><code className="rr-fv__mono">{file.link}</code></div>
        )}
      </Notice>
    );
  }

  return (
    <div className="rr-fv__renderer">
      <Toolbar>
        <ToolbarGroup>
          <SegmentedControl
            size="xs"
            value={fit}
            onChange={(v) => setFit(v as Fit)}
            data={[
              { label: labels.fitContain, value: 'contain' },
              { label: labels.fitCover, value: 'cover' },
              { label: labels.fitOriginal, value: 'original' }
            ]}
          />
        </ToolbarGroup>
        <ToolbarGroup>
          <ToolbarText>{labels.playbackSpeed}</ToolbarText>
          <Select size="xs" aria-label={labels.playbackSpeed} value={speed} onChange={(e) => setSpeed(e.currentTarget.value)}
            data={SPEEDS.map((s) => ({ value: s, label: `${s}×` }))} />
          <IconButton label={labels.loop} pressed={loop} active={loop} onClick={() => setLoop((v) => !v)}><IconLoop /></IconButton>
        </ToolbarGroup>
        {diag.levels.length > 0 && (
          <ToolbarGroup>
            <ToolbarText>{labels.quality}</ToolbarText>
            <Select size="xs" aria-label={labels.quality} value={String(diag.level)}
              onChange={(e) => { const next = Number(e.currentTarget.value); if (hls.current) hls.current.nextLevel = next; setDiag((d) => ({ ...d, level: next })); }}
              data={[{ value: '-1', label: labels.qualityAuto }, ...diag.levels.map((l) => ({ value: String(l.index), label: l.label }))]} />
          </ToolbarGroup>
        )}
        <ToolbarGroup>
          {pipAvailable && <IconButton label={labels.pictureInPicture} onClick={togglePip}><IconPip /></IconButton>}
          {canCapture && <IconButton label={labels.captureFrame} onClick={captureFrame}><IconCamera /></IconButton>}
        </ToolbarGroup>
      </Toolbar>
      <div className={`rr-fv__canvas rr-fv__canvas--video rr-fv__canvas--fit-${fit}`}>
        <video
          ref={video}
          className="rr-fv__video"
          controls
          playsInline
          preload="metadata"
          poster={source.thumbnail}
          onError={() => setFailed(labels.mediaPlaybackFailed)}
        >
          {labels.unsupportedMediaCodec}
        </video>
      </div>
    </div>
  );
}

function sameOrigin(url: string): boolean {
  try { return new URL(url, location.href).origin === location.origin; } catch { return false; }
}
